import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { lang } = useLang();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const t = {
    title: lang === "gr" ? "Νέος κωδικός" : "New password",
    desc: lang === "gr"
      ? "Επιλέξτε νέο κωδικό (τουλάχιστον 8 χαρακτήρες)."
      : "Choose a new password (at least 8 characters).",
    placeholder: lang === "gr" ? "Νέος κωδικός" : "New password",
    confirmPh: lang === "gr" ? "Επιβεβαίωση κωδικού" : "Confirm password",
    submit: lang === "gr" ? "Αλλαγή κωδικού" : "Update password",
    back: lang === "gr" ? "Επιστροφή στη σύνδεση" : "Back to sign in",
    mismatch: lang === "gr" ? "Οι κωδικοί δεν ταιριάζουν" : "Passwords don't match",
    tooShort: lang === "gr" ? "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες" : "Password must be at least 8 characters",
    success: lang === "gr" ? "Ο κωδικός άλλαξε. Συνδεθείτε." : "Password updated. Please sign in.",
    noToken: lang === "gr" ? "Λείπει ο σύνδεσμος επαναφοράς." : "Reset link is missing or invalid.",
  };

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-5">
        <div className="card-dark p-10 w-full max-w-md" data-testid="reset-no-token">
          <div className="label-tag mb-3 slash">Admin access</div>
          <h1 className="display-font text-3xl font-black uppercase mb-3">{t.title}</h1>
          <div className="mono-font text-xs text-[#ff003c] mb-5">{t.noToken}</div>
          <Link to="/admin/forgot-password" className="mono-font text-xs uppercase tracking-wider hover:text-[#d4ff00]">
            ← {t.back}
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) { setErr(t.tooShort); return; }
    if (pwd !== confirm) { setErr(t.mismatch); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success(t.success);
      nav("/admin/login");
    } catch (e2) {
      if (e2.code === "ECONNABORTED" || e2.message?.includes("timeout")) {
        setErr("Server unreachable. Please try again.");
      } else if (!e2.response) {
        setErr("Network error. Check your connection.");
      } else {
        const d = e2.response?.data?.detail;
        setErr(typeof d === "string" ? d : "Something went wrong");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5" data-testid="reset-password-page">
      <form onSubmit={submit} className="card-dark p-10 w-full max-w-md">
        <div className="label-tag mb-3 slash">Admin access</div>
        <h1 className="display-font text-3xl font-black uppercase mb-3">{t.title}</h1>
        <p className="text-sm text-[#a0a0aa] mb-6">{t.desc}</p>
        <input
          type="password" required placeholder={t.placeholder} className="input-dark mb-3"
          value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-password-input"
          minLength={8}
        />
        <input
          type="password" required placeholder={t.confirmPh} className="input-dark mb-5"
          value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm"
          minLength={8}
        />
        {err && <div className="mono-font text-xs text-[#ff003c] mb-3" data-testid="reset-error">{err}</div>}
        <button disabled={busy} className="btn-primary w-full justify-center mb-4" data-testid="reset-submit">
          {busy ? "..." : t.submit}
        </button>
        <Link to="/admin/login" className="mono-font text-xs uppercase tracking-wider hover:text-[#d4ff00]">
          ← {t.back}
        </Link>
      </form>
    </div>
  );
}
